/**
 * Permission Hook for Role-Based Access Control
 *
 * Provides computed permission properties based on user role.
 * Role hierarchy: viewer < supervisor < admin
 */

import { useMemo } from 'react';
import { useAuth, UserRole, VellrixUser } from '../contexts/AuthContext';

// Role hierarchy levels
const ROLE_HIERARCHY: Record<UserRole, number> = {
  viewer: 1,
  supervisor: 2,
  admin: 3,
};

export interface PermissionInfo {
  // Current user info
  user: VellrixUser | null;
  role: UserRole | null;
  isAuthenticated: boolean;

  // Role checks
  isViewer: boolean;
  isSupervisor: boolean;
  isAdmin: boolean;

  // Permission checks
  canViewAllTasks: boolean;
  canCreateTask: boolean;
  canUpdateAnyTaskStatus: boolean;
  canReassignAnyTask: boolean;
  canAcknowledgeAlert: boolean;
  canResolveAlert: boolean;
  canManageUsers: boolean;
  canSendPassdown: boolean;

  // Helper methods
  hasRole: (requiredRole: UserRole) => boolean;
  canAccessTask: (taskAssignedToUserId: number | null) => boolean;
  canUpdateTaskStatus: (taskAssignedToUserId: number | null) => boolean;
  canReassignTask: (taskAssignedToUserId: number | null) => boolean;
}

/**
 * Hook to get computed permission properties based on user role.
 *
 * Usage:
 * ```tsx
 * const { canCreateTask, canAcknowledgeAlert, isViewer } = usePermissions();
 *
 * if (!canCreateTask) {
 *   return <p>You don't have permission to create tasks.</p>;
 * }
 * ```
 */
export function usePermissions(): PermissionInfo {
  const { vellrixUser } = useAuth();

  return useMemo(() => {
    const role = vellrixUser?.role ?? null;
    const userId = vellrixUser?.id ?? null;

    // Role check helper
    const hasRole = (requiredRole: UserRole): boolean => {
      if (!role) return false;
      const userLevel = ROLE_HIERARCHY[role] ?? 0;
      const requiredLevel = ROLE_HIERARCHY[requiredRole] ?? 0;
      return userLevel >= requiredLevel;
    };

    // Role flags
    const isViewer = role === 'viewer';
    const isSupervisor = role === 'supervisor' || role === 'admin';
    const isAdmin = role === 'admin';

    // Permission flags based on role
    const canViewAllTasks = hasRole('supervisor');
    const canCreateTask = hasRole('supervisor');
    const canUpdateAnyTaskStatus = hasRole('supervisor');
    const canReassignAnyTask = hasRole('supervisor');
    const canAcknowledgeAlert = hasRole('supervisor');
    const canResolveAlert = hasRole('supervisor');
    const canManageUsers = hasRole('admin');
    const canSendPassdown = !!vellrixUser; // Any authenticated user

    // Task access helper - viewers can only access their assigned tasks
    const canAccessTask = (taskAssignedToUserId: number | null): boolean => {
      if (!vellrixUser) return false;
      if (hasRole('supervisor')) return true;
      return taskAssignedToUserId === userId;
    };

    // Task status update helper - viewers can only update their assigned tasks
    const canUpdateTaskStatus = (taskAssignedToUserId: number | null): boolean => {
      if (!vellrixUser) return false;
      if (hasRole('supervisor')) return true;
      return taskAssignedToUserId === userId;
    };

    // Task reassignment helper - viewers can only reassign their own tasks
    const canReassignTask = (taskAssignedToUserId: number | null): boolean => {
      if (!vellrixUser) return false;
      if (hasRole('supervisor')) return true;
      // Viewers can reassign tasks currently assigned to them
      return taskAssignedToUserId === userId;
    };

    return {
      user: vellrixUser,
      role,
      isAuthenticated: !!vellrixUser,

      isViewer,
      isSupervisor,
      isAdmin,

      canViewAllTasks,
      canCreateTask,
      canUpdateAnyTaskStatus,
      canReassignAnyTask,
      canAcknowledgeAlert,
      canResolveAlert,
      canManageUsers,
      canSendPassdown,

      hasRole,
      canAccessTask,
      canUpdateTaskStatus,
      canReassignTask,
    };
  }, [vellrixUser]);
}

export default usePermissions;
